import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Image, Video, Trash2, Upload, X, Check,
  FolderPlus, Loader2, Star, CheckSquare, Square, FolderInput,
  ChevronLeft, ChevronRight, Film, QrCode, Download, Printer, Users, Camera
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Masonry from 'react-masonry-css';
import { QRCodeSVG } from 'qrcode.react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminGalleryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [gallery, setGallery] = useState(null);
  const [content, setContent] = useState({ photos: [], videos: [], groups: [], photobooth_photos: [] });
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  
  // Video upload modals
  const [showVideoGalleryUploadModal, setShowVideoGalleryUploadModal] = useState(false);
  const [showTrailerUploadModal, setShowTrailerUploadModal] = useState(false);
  const [showVideoGiornataUploadModal, setShowVideoGiornataUploadModal] = useState(false);
  
  // Photo Booth state
  const [photoboothPhotos, setPhotoboothPhotos] = useState([]);
  const [showPhotoboothUploadModal, setShowPhotoboothUploadModal] = useState(false);
  const [uploadingPhotobooth, setUploadingPhotobooth] = useState(false);
  
  // Multi-selection state
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [showMoveModal, setShowMoveModal] = useState(false);

  useEffect(() => {
    fetchGallery();
    fetchContent();
  }, [id]);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/admin/galleries/${id}`);
      setGallery(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      if (error.response?.status === 404) {
        navigate('/admin');
      }
    }
  };

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/admin/galleries/${id}/content`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa foto?')) return;
    
    try {
      await axios.delete(`${API}/admin/galleries/${id}/photos/${photoId}`);
      fetchContent();
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Errore durante l\'eliminazione');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo video?')) return;
    
    try {
      await axios.delete(`${API}/admin/galleries/${id}/videos/${videoId}`);
      fetchContent();
    } catch (error) {
      console.error('Error deleting video:', error);
      alert('Errore durante l\'eliminazione');
    }
  };

  // Photo Booth functions
  const fetchPhotoboothPhotos = async () => {
    try {
      const response = await axios.get(`${API}/admin/galleries/${id}/content`);
      // Get photobooth photos from gallery raw data
      const galleryResponse = await axios.get(`${API}/admin/galleries/${id}`);
      // Fetch raw gallery to get photobooth_photos
      const rawGallery = await axios.get(`${API}/admin/galleries/${id}/content`);
      // For now we'll fetch them via a separate mechanism
    } catch (error) {
      console.error('Error fetching photobooth photos:', error);
    }
  };

  const handlePhotoboothUpload = async (files) => {
    setUploadingPhotobooth(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        await axios.post(`${API}/admin/galleries/${id}/photobooth/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (error) {
        console.error('Error uploading photobooth photo:', error);
      }
    }
    
    setUploadingPhotobooth(false);
    setShowPhotoboothUploadModal(false);
    // Refresh content
    fetchContent();
    fetchGallery();
  };

  const handleDeletePhotoboothPhoto = async (photoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa foto?')) return;
    
    try {
      await axios.delete(`${API}/admin/galleries/${id}/photobooth/${photoId}`);
      fetchContent();
      fetchGallery();
    } catch (error) {
      console.error('Error deleting photobooth photo:', error);
      alert('Errore durante l\'eliminazione');
    }
  };

  const handleSetCover = async (photoId) => {
    try {
      await axios.put(`${API}/admin/galleries/${id}/cover/${photoId}`);
      fetchGallery();
      alert('Copertina impostata');
    } catch (error) {
      console.error('Error setting cover:', error);
    }
  };

  // Multi-selection handlers
  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(photoId)) {
        newSet.delete(photoId);
      } else {
        newSet.add(photoId);
      }
      return newSet;
    });
  };

  const selectAllPhotos = () => {
    if (selectedPhotos.size === filteredPhotos.length) {
      setSelectedPhotos(new Set());
    } else {
      setSelectedPhotos(new Set(filteredPhotos.map(p => p.id)));
    }
  };

  const cancelSelection = () => {
    setIsSelecting(false);
    setSelectedPhotos(new Set());
  };

  const handleMovePhotos = async (targetGroupId) => {
    if (selectedPhotos.size === 0) return;
    
    try {
      await axios.put(`${API}/admin/galleries/${id}/photos/move`, {
        photo_ids: Array.from(selectedPhotos),
        target_group_id: targetGroupId
      });
      fetchContent();
      cancelSelection();
      setShowMoveModal(false);
    } catch (error) {
      console.error('Error moving photos:', error);
      alert('Errore durante lo spostamento delle foto');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPhotos.size === 0) return;
    if (!window.confirm(`Sei sicuro di voler eliminare ${selectedPhotos.size} foto?`)) return;
    
    try {
      await axios.post(`${API}/admin/galleries/${id}/photos/bulk-delete`, {
        photo_ids: Array.from(selectedPhotos)
      });
      fetchContent();
      cancelSelection();
    } catch (error) {
      console.error('Error deleting photos:', error);
      alert('Errore durante l\'eliminazione delle foto');
    }
  };

  // Filter and sort photos by filename
  const filteredPhotos = (selectedGroup === 'all' 
    ? content.photos 
    : content.photos.filter(p => p.group_id === selectedGroup)
  ).sort((a, b) => (a.original_filename || '').localeCompare(b.original_filename || ''));

  // Get videos by type
  const videoGallery = content.videos.find(v => v.video_type === 'video_gallery');
  const trailerVideo = content.videos.find(v => v.video_type === 'trailer');
  const videoGiornata = content.videos.find(v => v.video_type === 'video_giornata');

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = () => {
    setLightboxIndex((prev) => (prev === 0 ? filteredPhotos.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setLightboxIndex((prev) => (prev === filteredPhotos.length - 1 ? 0 : prev + 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <button 
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div className="w-9" />
          </div>
          
          <div className="text-center">
            <h1 className="font-wedding text-4xl text-gray-800">{gallery?.title}</h1>
            {gallery?.event_date && (
              <p className="text-sm text-gray-500 mt-1">
                {new Date(gallery.event_date).toLocaleDateString('it-IT', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            )}
          </div>
        </div>
      </header>

      {/* Selection Action Bar */}
      {isSelecting && (
        <div className="bg-blue-600 text-white sticky top-[73px] z-20">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={selectAllPhotos}
                  className="flex items-center gap-2 text-sm hover:bg-blue-700 px-3 py-1.5 rounded-lg transition-colors"
                >
                  {selectedPhotos.size === filteredPhotos.length ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  {selectedPhotos.size === filteredPhotos.length ? 'Deseleziona tutto' : 'Seleziona tutto'}
                </button>
                <span className="text-sm">
                  {selectedPhotos.size} foto selezionate
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                {selectedPhotos.size > 0 && (
                  <>
                    <button
                      onClick={() => setShowMoveModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium"
                    >
                      <FolderInput className="w-4 h-4" />
                      Sposta in gruppo
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
                    >
                      <Trash2 className="w-4 h-4" />
                      Elimina
                    </button>
                  </>
                )}
                <button
                  onClick={cancelSelection}
                  className="flex items-center gap-2 px-4 py-2 border border-white/30 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <X className="w-4 h-4" />
                  Annulla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-8">
        
        {/* ==================== SEZIONE 0: SOCIAL / QR CODE ==================== */}
        <QRCodeSection galleryId={id} galleryTitle={gallery?.title} />
        
        {/* ==================== SEZIONE 1: VIDEO GALLERY ==================== */}
        <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4">
            <h2 className="text-xl font-semibold text-white flex items-center gap-3">
              <Video className="w-6 h-6" />
              VIDEO GALLERY
            </h2>
          </div>
          
          <div className="p-6">
            {videoGallery ? (
              <div className="relative">
                <div className="aspect-video bg-gray-900 rounded-xl overflow-hidden">
                  <video 
                    src={videoGallery.url}
                    className="w-full h-full object-contain"
                    controls
                    preload="metadata"
                  />
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-gray-500">{videoGallery.original_filename}</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowVideoGalleryUploadModal(true)}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                    >
                      <Upload className="w-4 h-4" />
                      Sostituisci
                    </button>
                    <button
                      onClick={() => handleDeleteVideo(videoGallery.id)}
                      className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
                <Video className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Ancora nessun video caricato</p>
                <button
                  onClick={() => setShowVideoGalleryUploadModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                >
                  <Upload className="w-4 h-4" />
                  Carica Video Gallery
                </button>
              </div>
            )}
          </div>
        </section>

        {/* ==================== SEZIONE 2: VIDEO TRAILER ==================== */}
        <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-700 to-purple-900 px-6 py-4">
            <h2 className="text-xl font-semibold text-white flex items-center gap-3">
              <Film className="w-6 h-6" />
              VIDEO TRAILER
            </h2>
          </div>
          
          <div className="p-6">
            {trailerVideo ? (
              <div className="relative">
                <div className="aspect-video bg-gray-900 rounded-xl overflow-hidden">
                  <video 
                    src={trailerVideo.url}
                    className="w-full h-full object-contain"
                    controls
                    preload="metadata"
                  />
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-gray-500">{trailerVideo.original_filename}</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowTrailerUploadModal(true)}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                    >
                      <Upload className="w-4 h-4" />
                      Sostituisci
                    </button>
                    <button
                      onClick={() => handleDeleteVideo(trailerVideo.id)}
                      className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
                <Film className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Ancora nessun video caricato</p>
                <button
                  onClick={() => setShowTrailerUploadModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-700 text-white rounded-lg text-sm hover:bg-purple-800"
                >
                  <Upload className="w-4 h-4" />
                  Carica Video Trailer
                </button>
              </div>
            )}
          </div>
        </section>

        {/* ==================== SEZIONE 3: IL VIDEO DELLA GIORNATA ==================== */}
        <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-600 to-amber-800 px-6 py-4">
            <h2 className="text-xl font-semibold text-white flex items-center gap-3">
              <Video className="w-6 h-6" />
              IL VIDEO DELLA GIORNATA
            </h2>
          </div>
          
          <div className="p-6">
            {videoGiornata ? (
              <div className="relative">
                <div className="aspect-video bg-gray-900 rounded-xl overflow-hidden">
                  <video 
                    src={videoGiornata.url}
                    className="w-full h-full object-contain"
                    controls
                    preload="metadata"
                  />
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-gray-500">{videoGiornata.original_filename}</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowVideoGiornataUploadModal(true)}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                    >
                      <Upload className="w-4 h-4" />
                      Sostituisci
                    </button>
                    <button
                      onClick={() => handleDeleteVideo(videoGiornata.id)}
                      className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
                <Video className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Ancora nessun video caricato</p>
                <button
                  onClick={() => setShowVideoGiornataUploadModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700"
                >
                  <Upload className="w-4 h-4" />
                  Carica Video della Giornata
                </button>
              </div>
            )}
          </div>
        </section>

        {/* ==================== SEZIONE 4: FOTOGALLERY ==================== */}
        <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4">
            <h2 className="text-xl font-semibold text-white flex items-center gap-3">
              <Image className="w-6 h-6" />
              FOTOGALLERY
            </h2>
          </div>
          
          <div className="p-6">
            {/* Groups Section */}
            <GroupsSection 
              groups={content.groups} 
              galleryId={id}
              onUpdate={fetchContent}
              selectedGroup={selectedGroup}
              onSelectGroup={setSelectedGroup}
              onShowGroupModal={() => setShowGroupModal(true)}
            />
            
            {/* Actions */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-gray-500">{filteredPhotos.length} foto</p>
              <div className="flex items-center gap-2">
                {filteredPhotos.length > 0 && !isSelecting && (
                  <button
                    onClick={() => setIsSelecting(true)}
                    className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    <CheckSquare className="w-4 h-4" />
                    Seleziona
                  </button>
                )}
              </div>
            </div>
            
            {/* Upload Button - Always visible, centered */}
            <div className="flex justify-center mb-6">
              <button
                onClick={() => setShowPhotoUploadModal(true)}
                className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors"
              >
                <Upload className="w-5 h-5" />
                Carica Foto
              </button>
            </div>
            
            {/* Photos Grid */}
            {filteredPhotos.length === 0 ? (
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
                <Image className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nessuna foto in questo gruppo</p>
              </div>
            ) : (
              <Masonry
                breakpointCols={{
                  default: 5,
                  1280: 4,
                  1024: 3,
                  768: 2,
                  640: 2
                }}
                className="masonry-grid"
                columnClassName="masonry-grid-column"
              >
                {filteredPhotos.map((photo, index) => (
                  <PhotoCard 
                    key={photo.id} 
                    photo={photo}
                    groupName={content.groups.find(g => g.id === photo.group_id)?.name}
                    onDelete={() => handleDeletePhoto(photo.id)}
                    onSetCover={() => handleSetCover(photo.id)}
                    isCover={gallery?.cover_photo_id === photo.id}
                    isSelecting={isSelecting}
                    isSelected={selectedPhotos.has(photo.id)}
                    onToggleSelect={() => togglePhotoSelection(photo.id)}
                    onOpenLightbox={() => openLightbox(index)}
                  />
                ))}
              </Masonry>
            )}
          </div>
        </section>

        {/* ==================== SEZIONE 5: FOTO BOOT ==================== */}
        <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-fuchsia-600 to-pink-600 px-6 py-4">
            <h2 className="text-xl font-semibold text-white flex items-center gap-3">
              <Camera className="w-6 h-6" />
              FOTO BOOT
            </h2>
          </div>
          
          <div className="p-6">
            {/* Info bar */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-gray-500">{content.photobooth_photos?.length || 0} foto</p>
            </div>
            
            {/* Upload Button - Always visible, centered */}
            <div className="flex justify-center mb-6">
              <button
                onClick={() => setShowPhotoboothUploadModal(true)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white rounded-xl hover:opacity-90 transition-opacity"
              >
                <Upload className="w-5 h-5" />
                Carica Foto
              </button>
            </div>
            
            {/* Photos Grid */}
            {content.photobooth_photos?.length === 0 ? (
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
                <Camera className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nessuna foto nel Photo Booth</p>
              </div>
            ) : (
              <Masonry
                breakpointCols={{ default: 5, 1280: 4, 1024: 3, 768: 2 }}
                className="masonry-grid"
                columnClassName="masonry-grid-column"
              >
                {content.photobooth_photos?.map((photo) => (
                  <div key={photo.id} className="group relative rounded-xl overflow-hidden bg-gray-100 mb-4">
                    <img 
                      src={photo.thumbnail_url || photo.url}
                      alt=""
                      className="w-full h-auto block"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <button
                        onClick={() => handleDeletePhotoboothPhoto(photo.id)}
                        className="opacity-0 group-hover:opacity-100 p-3 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </Masonry>
            )}
          </div>
        </section>

      </div>

      {/* Lightbox */}
      {lightboxOpen && filteredPhotos.length > 0 && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white z-10"
          >
            <X className="w-6 h-6" />
          </button>
          
          <button
            onClick={goToPrevious}
            className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <img
            src={filteredPhotos[lightboxIndex]?.url}
            alt=""
            className="max-h-[90vh] max-w-[90vw] object-contain"
          />
          
          <button
            onClick={goToNext}
            className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
            {lightboxIndex + 1} / {filteredPhotos.length}
          </div>
        </div>
      )}

      {/* Modals */}
      {showGroupModal && (
        <CreateGroupModal 
          galleryId={id} 
          onClose={() => setShowGroupModal(false)}
          onCreated={() => { fetchContent(); setShowGroupModal(false); }}
        />
      )}

      {showPhotoUploadModal && (
        <PhotoUploadModal 
          galleryId={id}
          groups={content.groups}
          onClose={() => setShowPhotoUploadModal(false)}
          onUploaded={() => { fetchContent(); }}
        />
      )}

      {showVideoGalleryUploadModal && (
        <VideoUploadModal 
          galleryId={id}
          videoType="video_gallery"
          title="Carica Video Gallery"
          onClose={() => setShowVideoGalleryUploadModal(false)}
          onUploaded={() => { fetchContent(); setShowVideoGalleryUploadModal(false); }}
        />
      )}

      {showTrailerUploadModal && (
        <VideoUploadModal 
          galleryId={id}
          videoType="trailer"
          title="Carica Video Trailer"
          onClose={() => setShowTrailerUploadModal(false)}
          onUploaded={() => { fetchContent(); setShowTrailerUploadModal(false); }}
        />
      )}

      {showVideoGiornataUploadModal && (
        <VideoUploadModal 
          galleryId={id}
          videoType="video_giornata"
          title="Carica Video della Giornata"
          onClose={() => setShowVideoGiornataUploadModal(false)}
          onUploaded={() => { fetchContent(); setShowVideoGiornataUploadModal(false); }}
        />
      )}

      {showMoveModal && (
        <MovePhotosModal
          groups={content.groups}
          onClose={() => setShowMoveModal(false)}
          onMove={handleMovePhotos}
        />
      )}
      
      {/* Photo Booth Upload Modal */}
      {showPhotoboothUploadModal && (
        <PhotoboothUploadModal
          galleryId={id}
          onClose={() => setShowPhotoboothUploadModal(false)}
          onUploaded={() => {
            fetchContent();
            setShowPhotoboothUploadModal(false);
          }}
        />
      )}
    </div>
  );
};

// Groups Section Component
const GroupsSection = ({ groups, galleryId, onUpdate, selectedGroup, onSelectGroup, onShowGroupModal }) => {
  
  const handleDeleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Eliminare il gruppo "${groupName}"?\n\nLe foto del gruppo NON verranno eliminate e resteranno visibili in "Tutti i gruppi foto".`)) return;
    
    try {
      await axios.delete(`${API}/admin/galleries/${galleryId}/groups/${groupId}?keep_photos=true`);
      if (selectedGroup === groupId) {
        onSelectGroup('all');
      }
      onUpdate();
    } catch (error) {
      console.error('Error deleting group:', error);
      alert('Errore durante l\'eliminazione');
    }
  };

  return (
    <div className="mb-6">
      <h3 className="text-sm font-medium text-gray-700 mb-3">Gruppi</h3>
      <div className="flex flex-wrap gap-2">
        {/* Nuovo Gruppo button - yellow background */}
        <button
          onClick={onShowGroupModal}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors bg-yellow-400 text-black hover:bg-yellow-500"
        >
          <FolderPlus className="w-4 h-4" />
          Nuovo Gruppo
        </button>
        
        {/* Tutti i gruppi foto button */}
        <button
          onClick={() => onSelectGroup('all')}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            selectedGroup === 'all'
              ? 'bg-green-500 text-white'
              : 'bg-white border border-gray-200 hover:bg-gray-50'
          }`}
        >
          Tutti i gruppi foto
        </button>
        
        {groups.map(group => (
          <div 
            key={group.id}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
              selectedGroup === group.id
                ? 'bg-green-500 text-white'
                : 'bg-white border border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => onSelectGroup(group.id)}
          >
            <span>{group.name}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteGroup(group.id, group.name);
              }}
              className={`p-1 rounded transition-colors ${
                selectedGroup === group.id
                  ? 'text-white/70 hover:text-white hover:bg-green-600'
                  : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
              }`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Photo Card Component
const PhotoCard = ({ photo, groupName, onDelete, onSetCover, isCover, isSelecting, isSelected, onToggleSelect, onOpenLightbox }) => {
  const displayUrl = photo.thumbnail_url || photo.url;

  const handleClick = () => {
    if (isSelecting) {
      onToggleSelect();
    } else if (onOpenLightbox) {
      onOpenLightbox();
    }
  };

  return (
    <div 
      className={`group relative rounded-xl overflow-hidden bg-gray-100 cursor-pointer ${
        isSelected ? 'ring-4 ring-blue-500' : ''
      }`}
      onClick={handleClick}
    >
      <img 
        src={displayUrl} 
        alt={photo.original_filename}
        className="w-full h-auto block"
        loading="lazy"
      />
      
      {isSelecting && (
        <div className="absolute top-2 left-2 z-10">
          {isSelected ? (
            <div className="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
          ) : (
            <div className="w-6 h-6 bg-white/80 rounded-md border-2 border-gray-300" />
          )}
        </div>
      )}
      
      {!isSelecting && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors pointer-events-none">
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 pointer-events-auto">
            <button 
              onClick={(e) => { e.stopPropagation(); onSetCover(); }}
              className="p-2 bg-white rounded-lg hover:bg-gray-100"
              title="Imposta come copertina"
            >
              <Star className={`w-4 h-4 ${isCover ? 'text-yellow-500 fill-yellow-500' : ''}`} />
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-2 bg-white rounded-lg hover:bg-gray-100 text-red-500"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {isCover && !isSelecting && (
        <div className="absolute top-2 left-2 px-2 py-1 bg-yellow-500 text-white text-xs rounded-full">
          Copertina
        </div>
      )}
    </div>
  );
};

// Create Group Modal
const CreateGroupModal = ({ galleryId, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/admin/galleries/${galleryId}/groups`, { name });
      onCreated();
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Errore durante la creazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold mb-4">Nuovo Gruppo</h2>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="es. Casa Sposo, Cerimonia, Ricevimento..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 mb-4"
            autoFocus
          />
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="flex-1 px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Crea
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Photo Upload Modal
const PhotoUploadModal = ({ galleryId, groups, onClose, onUploaded }) => {
  const [selectedGroup, setSelectedGroup] = useState(groups[0]?.id || '');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const abortRef = useRef(null);
  const cancelledRef = useRef(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
    setFiles(prev => [...prev, ...droppedFiles]);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (file, index) => {
    try {
      setProgress(prev => ({ ...prev, [index]: 0 }));
      
      const formData = new FormData();
      formData.append('file', file);
      
      abortRef.current = new AbortController();
      
      await axios.post(
        `${API}/admin/galleries/${galleryId}/photos/upload?group_id=${selectedGroup}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          signal: abortRef.current.signal,
          onUploadProgress: (e) => {
            const percent = Math.round((e.loaded * 100) / e.total);
            setProgress(prev => ({ ...prev, [index]: percent }));
          }
        }
      );
      
      setProgress(prev => ({ ...prev, [index]: 100 }));
      return true;
    } catch (error) {
      if (error.code === 'ERR_CANCELED') {
        setProgress(prev => {
          const cp = { ...prev };
          delete cp[index];
          return cp;
        });
        return false;
      }
      console.error(`Error uploading ${file.name}:`, error);
      setProgress(prev => ({ ...prev, [index]: -1 }));
      return false;
    }
  };

  const handleCancelUpload = () => {
    cancelledRef.current = true;
    abortRef.current?.abort();
  };

  const handleUpload = async () => {
    if (files.length === 0 || !selectedGroup) return;
    
    setUploading(true);
    cancelledRef.current = false;
    
    for (let i = 0; i < files.length; i++) {
      if (cancelledRef.current) break;
      await uploadFile(files[i], i);
    }
    
    setUploading(false);
    onUploaded();
    
    if (cancelledRef.current) return;
    
    const allSuccess = Object.values(progress).every(p => p === 100);
    if (allSuccess) {
      setTimeout(onClose, 500);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Carica Foto</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="p-6 flex-1 overflow-y-auto">
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Gruppo di destinazione</label>
            {groups.length === 0 ? (
              <p className="text-sm text-red-500">Crea prima un gruppo per poter caricare le foto</p>
            ) : (
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
              >
                {groups.map(group => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
              </select>
            )}
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              dragOver ? 'border-black bg-gray-50' : 'border-gray-300'
            }`}
          >
            <Upload className="w-10 h-10 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">Trascina qui le foto</p>
            <p className="text-sm text-gray-400 mb-4">oppure</p>
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              Seleziona file
            </label>
          </div>

          {files.length > 0 && (
            <div className="mt-6 space-y-2 max-h-48 overflow-y-auto">
              {files.map((file, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg" data-testid={`upload-file-row-${index}`}>
                  <div className="flex items-center gap-3">
                    <Image className="w-5 h-5 text-gray-400" />
                    <span className="flex-1 truncate text-sm">{file.name}</span>
                    {progress[index] !== undefined ? (
                      progress[index] === -1 ? (
                        <span className="text-red-500 text-sm">Errore</span>
                      ) : progress[index] === 100 ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <span className="text-sm text-gray-500">{progress[index]}%</span>
                      )
                    ) : (
                      <button onClick={() => removeFile(index)} className="p-1 hover:bg-gray-200 rounded">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {progress[index] !== undefined && progress[index] >= 0 && progress[index] < 100 && (
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className="bg-black h-1.5 rounded-full transition-all duration-200"
                        style={{ width: `${progress[index]}%` }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100">
          {uploading && (
            <div className="mb-4" data-testid="upload-overall-progress">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-600">Progresso totale</span>
                <span className="font-medium">
                  {files.length ? Math.round(Object.values(progress).reduce((a, p) => a + Math.max(0, p), 0) / files.length) : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-black h-2 rounded-full transition-all duration-200"
                  style={{ width: `${files.length ? Math.round(Object.values(progress).reduce((a, p) => a + Math.max(0, p), 0) / files.length) : 0}%` }}
                />
              </div>
            </div>
          )}
          <button
            onClick={handleUpload}
            disabled={files.length === 0 || !selectedGroup || uploading}
            className="w-full py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Caricamento in corso...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Carica {files.length} foto
              </>
            )}
          </button>
          {uploading && (
            <button
              onClick={handleCancelUpload}
              data-testid="cancel-upload-btn"
              className="w-full mt-3 py-2.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center justify-center gap-2 text-sm"
            >
              <X className="w-4 h-4" />
              Annulla caricamento
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Video Upload Modal
const VideoUploadModal = ({ galleryId, videoType, title, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const abortRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = Array.from(e.dataTransfer.files).find(f => f.type.startsWith('video/'));
    if (droppedFile) setFile(droppedFile);
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('video_type', videoType);
      
      abortRef.current = new AbortController();
      
      await axios.post(
        `${API}/admin/galleries/${galleryId}/videos/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          signal: abortRef.current.signal,
          onUploadProgress: (e) => {
            const percent = Math.round((e.loaded * 100) / e.total);
            setProgress(percent);
          }
        }
      );
      
      onUploaded();
    } catch (error) {
      if (error.code === 'ERR_CANCELED') {
        setProgress(0);
      } else {
        console.error('Error uploading video:', error);
        alert('Errore durante il caricamento del video');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    abortRef.current?.abort();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            dragOver ? 'border-black bg-gray-50' : 'border-gray-300'
          }`}
        >
          {file ? (
            <div className="space-y-4">
              <Video className="w-12 h-12 text-green-500 mx-auto" />
              <p className="font-medium">{file.name}</p>
              <p className="text-sm text-gray-500">
                {(file.size / (1024 * 1024)).toFixed(1)} MB
              </p>
              {!uploading && (
                <button
                  onClick={() => setFile(null)}
                  className="text-sm text-red-500 hover:underline"
                >
                  Rimuovi
                </button>
              )}
            </div>
          ) : (
            <>
              <Video className="w-10 h-10 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">Trascina qui il video</p>
              <p className="text-sm text-gray-400 mb-4">oppure</p>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors">
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                Seleziona file
              </label>
            </>
          )}
        </div>

        {uploading && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span>Caricamento in corso...</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-black h-2 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <button
            onClick={uploading ? handleCancelUpload : onClose}
            data-testid="video-cancel-btn"
            className={`flex-1 px-4 py-3 border rounded-lg ${
              uploading 
                ? 'border-red-300 text-red-600 hover:bg-red-50' 
                : 'border-gray-300 hover:bg-gray-50'
            }`}
          >
            {uploading ? 'Annulla caricamento' : 'Annulla'}
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="flex-1 px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Upload className="w-5 h-5" />
            )}
            {uploading ? 'Caricamento...' : 'Carica'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Move Photos Modal
const MovePhotosModal = ({ groups, onClose, onMove }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold mb-4">Sposta in gruppo</h2>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {groups.map(group => (
            <button
              key={group.id}
              onClick={() => onMove(group.id)}
              className="w-full px-4 py-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {group.name}
            </button>
          ))}
        </div>
        <button
          onClick={onClose}
          className="w-full mt-4 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Annulla
        </button>
      </div>
    </div>
  );
};

// QR Code Section Component
const QRCodeSection = ({ galleryId, galleryTitle }) => {
  const qrRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  
  // Generate the public URL for guests
  const baseUrl = window.location.origin;
  const guestPageUrl = `${baseUrl}/event/${galleryId}`;
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(guestPageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    
    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `qr-code-${galleryTitle || 'evento'}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };
  
  const handlePrint = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${galleryTitle || 'Evento'}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: 'Georgia', serif;
              background: white;
            }
            .container {
              text-align: center;
              padding: 40px;
            }
            h1 {
              font-size: 28px;
              margin-bottom: 10px;
              color: #333;
            }
            .subtitle {
              font-size: 16px;
              color: #666;
              margin-bottom: 30px;
            }
            .qr-container {
              padding: 20px;
              background: white;
              border: 2px solid #e5e5e5;
              border-radius: 12px;
              display: inline-block;
              margin-bottom: 20px;
            }
            .instructions {
              font-size: 14px;
              color: #888;
              max-width: 300px;
              line-height: 1.5;
            }
            @media print {
              body { -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>${galleryTitle || 'Il Nostro Matrimonio'}</h1>
            <p class="subtitle">Condividi i tuoi momenti!</p>
            <div class="qr-container">
              ${svgData}
            </div>
            <p class="instructions">
              Scansiona il QR code con la fotocamera del tuo telefono per caricare le tue foto dell'evento
            </p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };
  
  return (
    <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-pink-600 to-purple-700 px-6 py-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white flex items-center gap-3">
          <Users className="w-6 h-6" />
          INSTAWEDDING
        </h2>
        <button
          onClick={() => setShowManageModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Image className="w-4 h-4" />
          Gestione Instawedding
        </button>
      </div>
      
      <div className="p-6">
        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* QR Code */}
          <div 
            ref={qrRef}
            className="flex-shrink-0 p-6 bg-white border-2 border-gray-100 rounded-2xl shadow-sm"
          >
            <QRCodeSVG 
              value={guestPageUrl}
              size={200}
              level="H"
              includeMargin={true}
              bgColor="#ffffff"
              fgColor="#000000"
            />
          </div>
          
          {/* Info and Actions */}
          <div className="flex-1 text-center lg:text-left">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Pagina Caricamento Ospiti
            </h3>
            <p className="text-gray-600 mb-4">
              Gli ospiti possono scansionare questo QR code per caricare le loro foto dell'evento. 
              Nessun login richiesto!
            </p>
            
            {/* URL Display */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 flex items-center gap-2 max-w-md mx-auto lg:mx-0">
              <input 
                type="text" 
                value={guestPageUrl} 
                readOnly 
                className="flex-1 bg-transparent text-sm text-gray-600 truncate outline-none"
              />
              <button
                onClick={handleCopyLink}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  copied 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                {copied ? 'Copiato!' : 'Copia'}
              </button>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
              <button
                onClick={handleDownloadQR}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
              >
                <Download className="w-4 h-4" />
                Scarica QR
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
              >
                <Printer className="w-4 h-4" />
                Stampa
              </button>
              <a
                href={guestPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-700 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
              >
                <QrCode className="w-4 h-4" />
                Apri Pagina
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Manage Instawedding Modal */}
      {showManageModal && (
        <ManageInstaweddingModal 
          galleryId={galleryId}
          onClose={() => setShowManageModal(false)}
        />
      )}
    </section>
  );
};

// Manage Instawedding Modal Component
const ManageInstaweddingModal = ({ galleryId, onClose }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [zipDownloading, setZipDownloading] = useState(false);

  const handleDownloadAllZip = async () => {
    if (zipDownloading || photos.length === 0) return;
    setZipDownloading(true);
    try {
      const res = await axios.get(`${API}/galleries/${galleryId}/guest-photos/download-zip`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'instawedding.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('ZIP download error:', error);
      alert('Errore durante il download del file ZIP');
    } finally {
      setZipDownloading(false);
    }
  };

  useEffect(() => {
    fetchGuestPhotos();
  }, []);

  const fetchGuestPhotos = async () => {
    try {
      const res = await axios.get(`${API}/public/event/${galleryId}`);
      setPhotos(res.data.photos || []);
    } catch (error) {
      console.error('Error fetching guest photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa foto?')) return;
    
    setDeleting(photoId);
    try {
      await axios.delete(`${API}/admin/galleries/${galleryId}/guest-photos/${photoId}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Errore durante l\'eliminazione');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-600 to-purple-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white flex items-center gap-3">
            <Camera className="w-6 h-6" />
            Gestione Instawedding
          </h2>
          <div className="flex items-center gap-2">
            {photos.length > 0 && (
              <button
                onClick={handleDownloadAllZip}
                disabled={zipDownloading}
                data-testid="instawedding-download-zip-btn"
                className="flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
              >
                {zipDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {zipDownloading ? 'Preparazione...' : 'Scarica tutto (ZIP)'}
              </button>
            )}
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-12">
              <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Nessuna foto degli ospiti</p>
              <p className="text-gray-400 text-sm mt-1">Le foto caricate dagli ospiti appariranno qui</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">{photos.length} foto caricate dagli ospiti</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {photos.map((photo) => (
                  <div 
                    key={photo.id}
                    className="group relative rounded-xl overflow-hidden bg-gray-100 aspect-square"
                  >
                    {photo.media_type === 'video' ? (
                      <video 
                        src={photo.url}
                        className="w-full h-full object-cover"
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img 
                        src={photo.thumbnail_url || photo.url}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                    
                    {/* Overlay with info and delete */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                      <button
                        onClick={() => handleDelete(photo.id)}
                        disabled={deleting === photo.id}
                        className="opacity-0 group-hover:opacity-100 p-3 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all disabled:opacity-50"
                      >
                        {deleting === photo.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Trash2 className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    
                    {/* Guest name badge */}
                    {photo.guest_name && (
                      <div className="absolute bottom-2 left-2 right-2">
                        <span className="bg-black/60 text-white text-xs px-2 py-1 rounded-full truncate block text-center">
                          {photo.guest_name}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};

// Photo Booth Upload Modal
const PhotoboothUploadModal = ({ galleryId, onClose, onUploaded }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);
  const cancelledRef = useRef(false);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    cancelledRef.current = false;
    let uploaded = 0;
    
    for (const file of files) {
      if (cancelledRef.current) break;
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        abortRef.current = new AbortController();
        
        await axios.post(`${API}/admin/galleries/${galleryId}/photobooth/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          signal: abortRef.current.signal,
          onUploadProgress: (e) => {
            const frac = e.total ? e.loaded / e.total : 0;
            setProgress(Math.round(((uploaded + frac) / files.length) * 100));
          }
        });
        
        uploaded++;
        setProgress(Math.round((uploaded / files.length) * 100));
      } catch (error) {
        if (error.code === 'ERR_CANCELED') break;
        console.error('Error uploading:', error);
      }
    }
    
    setUploading(false);
    if (cancelledRef.current) {
      setProgress(0);
      return;
    }
    onUploaded();
  };

  const handleCancelUpload = () => {
    cancelledRef.current = true;
    abortRef.current?.abort();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-fuchsia-600 to-pink-600 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white flex items-center gap-3">
            <Camera className="w-6 h-6" />
            Carica Foto Booth
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {/* File Input */}
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-fuchsia-400 transition-colors"
          >
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">Clicca per selezionare le foto</p>
            <p className="text-gray-400 text-sm mt-1">oppure trascina i file qui</p>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          
          {/* Selected Files */}
          {files.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">{files.length} foto selezionate</p>
              <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                {files.map((file, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={URL.createObjectURL(file)}
                      alt=""
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                    <button
                      onClick={() => removeFile(index)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Progress */}
          {uploading && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                <span>Caricamento in corso...</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-fuchsia-500 to-pink-500 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
        
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button
            onClick={uploading ? handleCancelUpload : onClose}
            data-testid="photobooth-cancel-btn"
            className={`flex-1 py-3 border rounded-lg font-medium transition-colors ${
              uploading 
                ? 'border-red-300 text-red-600 hover:bg-red-50' 
                : 'border-gray-300 hover:bg-gray-50'
            }`}
          >
            {uploading ? 'Annulla caricamento' : 'Annulla'}
          </button>
          <button
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
            className="flex-1 py-3 bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {uploading ? 'Caricamento...' : `Carica ${files.length} foto`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminGalleryDetail;
